"use client";

import { useEffect, useState } from "react";
import { Icon } from "@/lib/icons/Icon";
import { FormStatus, SubmitButton } from "@/components/ui/Form";
import { toast } from "@/lib/notifications/toast";
import {
  acceptPanelRegulamin,
  fetchPanelRegulamin,
  type PanelRegulamin,
} from "@/lib/api/c-crm-project-management-user";
import styles from "./CCrmProjectManagement.module.css";

/**
 * Bramka regulaminu panelu klienta (plugin c_crm_project_management).
 *
 * Gdy operator włączył wymóg akceptacji, klient przed wejściem widzi treść regulaminu
 * i prośbę o zgodę - projekty, zadania i usługi pokazują się dopiero po jej udzieleniu.
 *
 * **Bramka jest wyłącznie interfejsem, nie zabezpieczeniem.** Endpointy danych i tak
 * sprawdzają uprawnienia po swojej stronie; tutaj chodzi o to, żeby klient nie korzystał
 * z panelu, nie znając zasad.
 *
 * Wymóg i stan zgody liczy backend - front nie decyduje, czy regulamin obowiązuje,
 * bo ta decyzja ma jedno źródło (konfiguracja katalogu) i musi być taka sama dla
 * każdego klienta, niezależnie od tego, co ma w przeglądarce.
 *
 * Dopóki stan nie jest znany, NIE renderujemy panelu - inaczej klient zobaczyłby
 * na moment dane, których zgodnie z konfiguracją nie powinien jeszcze widzieć.
 */
export function RegulaminGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PanelRegulamin | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPanelRegulamin();
        if (cancelled) return;
        setState(data);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function accept() {
    setSaving(true);
    try {
      const result = await acceptPanelRegulamin();
      setState((prev) =>
        prev ? { ...prev, accepted: true, accept_date: result.accept_date } : prev,
      );
      toast.success("Regulamin zaakceptowany.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <FormStatus variant="info">Wczytywanie...</FormStatus>;

  /* Błąd sprawdzenia NIE wpuszcza do panelu na skróty: skoro nie wiadomo, czy regulamin
     obowiązuje, to bezpieczniejszym wyjściem jest komunikat niż ciche pokazanie danych. */
  if (error) return <FormStatus variant="error">{error}</FormStatus>;

  if (!state?.required || state.accepted) return <>{children}</>;

  return (
    <div className={styles.root}>
      <div className={styles.panelHeader}>
        <h3 className={styles.panelTitle}>Regulamin panelu klienta</h3>
      </div>

      <FormStatus variant="info">
        Zanim przejdziesz do projektów, zapoznaj się z regulaminem i zaakceptuj go.
      </FormStatus>

      {/* Treść pochodzi z konfiguracji panelu i jest sanityzowana po stronie serwera -
          renderujemy ją jak pozostałe treści redakcyjne w CMS. */}
      <div
        className={`${styles.richText} ${styles.regulaminText}`}
        dangerouslySetInnerHTML={{ __html: state.text }}
      />

      <label className={styles.regulaminAccept}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          disabled={saving}
        />
        <span>Zapoznałem się z regulaminem i akceptuję jego treść</span>
      </label>

      <div>
        <SubmitButton
          type="button"
          className={styles.regulaminAcceptButton}
          onClick={accept}
          disabled={!checked}
          loading={saving}
        >
          <Icon name="check" />
          Akceptuję i przechodzę do panelu
        </SubmitButton>
      </div>
    </div>
  );
}
